import React, { useState, useEffect } from "react";
import kycSuccess from "assets/images/kyc-success.png";
import { Formik, Form, ErrorMessage } from "formik";
import { routes } from "routes/apiRoutes";
import makeRequest from "services/makeRequest";
import { useSelector } from "react-redux";
import {
  FileUpload,
  FormError,
  Typography,
  NotificationModal,
  Loader,
} from "components";
import { useNavigate } from "react-router-dom";
import { ROUTES, fileBaseURL as baseURL } from "routes/routePaths";
import ButtonPair from "./ButtonPair";
import { isArray, getAgentId, isSuccessResponse, setAlert } from "utils/helper";
import { KYCDocumentsValidation } from "utils/FormValidations";

import guarantorForm from "assets/documnets/guarantorForm.pdf";

const FileUploads = ({ handlePrevious }) => {
  const navigate = useNavigate();

  const [isCorporate, setIsCorportate] = useState(true);
  const [guarantorId, setGuarantorId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showGuarantorDoc, setShowGuarantorDoc] = useState(true);
  const [filesUploaded, setFilesUploaded] = useState({});

  const user = useSelector((state) => state.user);

  //existing agent details would return agentId from token else, we check if there's any in the localstorage
  const agentId = user.user.agent_id || getAgentId();

  useEffect(() => {
    setLoading(true);
    const getAgentInfo = async () => {
      try {
        const personalInfo = await makeRequest(
          "get",
          routes.getAgentPersonalDetails(`agentId=${agentId}`)
        );

        if (isSuccessResponse(personalInfo.data)) {
          const {
            corporate,
            formC07,
            businessRegForm,
            uploadedId,
            uploadedPassport,
            uploadedUtility,
            validId,
            validPassport,
            validUtility,
            validBusinessRegForm,
            validFormC07,
          } = personalInfo.data?.data?.content[0].agentDetails;
          setIsCorportate(corporate);

          let filesUploaded = {
            formC07,
            businessRegForm,
            uploadedId,
            uploadedPassport,
            uploadedUtility,

            state: {
              validId,
              validPassport,
              validUtility,
              validBusinessRegForm,
              validFormC07,
            },
          };

          const { data } = await makeRequest(
            "get",
            routes.guarantorInfo(agentId)
          );

          if (isSuccessResponse(data)) {
            if (data?.data?.length) {
              setGuarantorId(data?.data[0].id);

              const {
                uploadedForm,
                uploadedId: guarantorId,
                validId,
                validForm,
              } = data?.data[0];

              const guarantorDocs = {
                uploadedForm,
                guarantorId,
                state: { validId, validForm },
              };

              filesUploaded.guarantorDocs = guarantorDocs;

              // setLoading(false);
            } else {
              //dont show guarantor doc upload since user didnt provide any guarantor detail yet
              setShowGuarantorDoc(false);
            }
            setLoading(false);
          }

          setFilesUploaded(filesUploaded);
          console.log(filesUploaded);
        }
      } catch (err) {
        console.log(err);
      }
    };
    getAgentInfo();
  }, [agentId]);

  const initialValues = {
    agentIdentification: "",
    utilityBill: "",
    formC07: "", //only corporate
    passport: "",
    registrationProof: "", //only corporate
    guarantorIdentification: "",
    guarantorForm: "",
  };

  const onSubmit = async (values) => {
    const {
      agentIdentification,
      utilityBill,
      formC07,
      passport,
      guarantorForm,
      guarantorIdentification,
      registrationProof,
    } = values;
    const uploadedFiles = new FormData();
    if (agentIdentification) {
      uploadedFiles.append("agentIdentification", agentIdentification);
    }

    if (utilityBill) {
      uploadedFiles.append("utilityBill", utilityBill);
    }

    if (formC07) {
      uploadedFiles.append("formC07", formC07);
    }

    if (passport) {
      uploadedFiles.append("passport", passport);
    }

    if (registrationProof) {
      uploadedFiles.append("registrationProof", registrationProof);
    }

    if (guarantorIdentification || guarantorForm) {
      uploadedFiles.append("guarantor[0].id", guarantorId); //This is to track update

      if (guarantorIdentification) {
        uploadedFiles.append(
          "guarantor[0].guarantorIdentification",
          guarantorIdentification
        );
      }

      if (guarantorForm) {
        uploadedFiles.append("guarantor[0].guarantorForm", guarantorForm);
      }
    }

    try {
      const { data } = await makeRequest(
        "post",
        routes.uploadKYCFiles(agentId),
        uploadedFiles
      );
      if (isSuccessResponse(data)) {
        localStorage.setItem("onBoardingDone", true);
        setShowModal(true);
      } else {
        setAlert(data.responseMessage);
      }
    } catch (error) {
      console.log(error);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center onboarding-loader">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={KYCDocumentsValidation({
          //Specific validations for only when agent had not even initially uploaded
          //We'd have these values as either undefined or false from endpoint which are both falsy values
          //Or Admin has deemed uploaded as invalid
          validateAgentId: !filesUploaded.state?.validId,
          validateUtilityBill: !filesUploaded.state?.validUtility,
          validateAgentPassport: !filesUploaded.state?.validPassport,
          validateFormC07:
            isCorporate && !filesUploaded.state?.validBusinessRegForm,
        })}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form noValidate autoComplete="off">
            <div>
              <Typography
                style={{ margin: "3rem 0 1rem" }}
                variant="subtitle3"
                state="primary"
              >
                Agent's Documents
              </Typography>

              <FileUpload
                label={
                  isCorporate
                    ? "Upload Corporate contact Person's means of Identification"
                    : "Upload ID card selected in Personal Details"
                }
                onChange={(file) => {
                  setFieldValue(
                    "agentIdentification",
                    isArray(file) ? file[0] : file
                  );
                }}
                acceptedFormatsText={"JPG, PNG, JPEG"}
                uploadedFile={values.agentIdentification}
                acceptedFormats={"image/*"}
                disabled={filesUploaded.state?.validId}
                required={!filesUploaded.state?.validId}
              />
              <ErrorMessage component={FormError} name="agentIdentification" />
              {filesUploaded.uploadedId && (
                <a
                  href={`${baseURL}/${filesUploaded.uploadedId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Typography
                    variant="caption2"
                    className={"text-underline cursor-pointer"}
                  >
                    View Previous Upload
                  </Typography>
                </a>
              )}

              <FileUpload
                label={
                  isCorporate
                    ? "Upload Corporate Utility bill"
                    : "Upload Utility Bill"
                }
                onChange={(file) => {
                  setFieldValue("utilityBill", isArray(file) ? file[0] : file);
                }}
                className={"mt-3"}
                acceptedFormatsText={"JPG, PNG, JPEG, PDF"}
                uploadedFile={values.utilityBill}
                acceptedFormats={"image/*, application/pdf"}
                disabled={filesUploaded.state?.validUtility}
                required={!filesUploaded.state?.validUtility}
              />
              <ErrorMessage component={FormError} name="utilityBill" />
              {filesUploaded.uploadedUtility && (
                <a
                  href={`${baseURL}/${filesUploaded.uploadedUtility}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Typography
                    variant="caption2"
                    className={"text-underline cursor-pointer"}
                  >
                    View Previous Upload
                  </Typography>
                </a>
              )}
              <FileUpload
                label="Upload Passport photograph"
                onChange={(file) => {
                  setFieldValue("passport", isArray(file) ? file[0] : file);
                }}
                className={"mt-3"}
                acceptedFormatsText={"JPG, PNG, JPEG"}
                uploadedFile={values.passport}
                acceptedFormats={"image/*"}
                disabled={filesUploaded.state?.validPassport}
                required={!filesUploaded.state?.validPassport}
              />
              <ErrorMessage component={FormError} name="passport" />
              {filesUploaded.uploadedPassport && (
                <a
                  href={`${baseURL}/${filesUploaded.uploadedPassport}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Typography
                    variant="caption2"
                    className={"text-underline cursor-pointer"}
                  >
                    View Previous Upload
                  </Typography>
                </a>
              )}
              {isCorporate && (
                <>
                  <FileUpload
                    label="Upload Corporate’s form C07 for PLC/LTD or document showing list of Directors."
                    onChange={(file) => {
                      setFieldValue("formC07", isArray(file) ? file[0] : file);
                    }}
                    className={"mt-3"}
                    acceptedFormatsText={"JPG, JPEG, PNG, Doc, Docx, PDF"}
                    uploadedFile={values.formC07}
                    required={false}
                    acceptedFormats={"image/*, .doc, .docx, application/pdf"}
                    disabled={filesUploaded.state?.validFormC07}
                  />
                  <ErrorMessage component={FormError} name="formC07" />
                  {filesUploaded.formC07 && (
                    <a
                      href={`${baseURL}/${filesUploaded.formC07}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography
                        variant="caption2"
                        className={"text-underline cursor-pointer"}
                      >
                        View Previous Upload
                      </Typography>
                    </a>
                  )}

                  <FileUpload
                    label="Upload proof of registration/CAC"
                    onChange={(file) => {
                      setFieldValue(
                        "registrationProof",
                        isArray(file) ? file[0] : file
                      );
                    }}
                    className={"mt-3"}
                    acceptedFormatsText={"JPG, JPEG, PNG, Doc, Docx, PDF"}
                    uploadedFile={values.registrationProof}
                    acceptedFormats={"image/*, .doc, .docx, application/pdf"}
                    disabled={filesUploaded.state?.validBusinessRegForm}
                    required={!filesUploaded.state?.validBusinessRegForm}
                  />
                  <ErrorMessage
                    component={FormError}
                    name="registrationProof"
                  />
                  {filesUploaded.businessRegForm && (
                    <a
                      href={`${baseURL}/${filesUploaded.businessRegForm}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography
                        variant="caption2"
                        className={"text-underline cursor-pointer"}
                      >
                        View Previous Upload
                      </Typography>
                    </a>
                  )}
                </>
              )}

              {showGuarantorDoc && (
                <>
                  <Typography
                    style={{ margin: "3rem 0 1rem" }}
                    variant="subtitle3"
                    state="primary"
                  >
                    Guarantor's Documents
                  </Typography>

                  <FileUpload
                    label="Upload Guarantor ID"
                    onChange={(file) => {
                      setFieldValue(
                        "guarantorIdentification",
                        isArray(file) ? file[0] : file
                      );
                    }}
                    acceptedFormatsText={"JPG, PNG, JPEG"}
                    uploadedFile={values.guarantorIdentification}
                    acceptedFormats={"image/*"}
                    required={
                      // !filesUploaded.guarantorDocs?.state?.validId
                      //Guarantor details are never on "REUQUIRED", hence no condition
                      false
                    }
                    disabled={filesUploaded.guarantorDocs?.state?.validId}
                  />
                  <ErrorMessage
                    component={FormError}
                    name="guarantorIdentification"
                  />
                  {filesUploaded.guarantorDocs?.guarantorId && (
                    <a
                      href={`${baseURL}/${filesUploaded.guarantorDocs?.guarantorId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography
                        variant="caption2"
                        className={"text-underline cursor-pointer"}
                      >
                        View Previous Upload
                      </Typography>
                    </a>
                  )}

                  <FileUpload
                    label="Upload Guarantor Agreement Form"
                    onChange={(file) => {
                      setFieldValue(
                        "guarantorForm",
                        isArray(file) ? file[0] : file
                      );
                    }}
                    className={"mt-3"}
                    required={false}
                    acceptedFormatsText={"JPG, JPEG, PNG, Doc, Docx, PDF"}
                    uploadedFile={values.guarantorForm}
                    acceptedFormats={"image/*, .doc, .docx, application/pdf"}
                    disabled={filesUploaded.guarantorDocs?.state?.validForm}
                  />
                  <ErrorMessage component={FormError} name="guarantorForm" />
                  {filesUploaded.guarantorDocs?.uploadedForm && (
                    <a
                      href={`${baseURL}/${filesUploaded.guarantorDocs?.uploadedForm}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography
                        variant="caption2"
                        className={"text-underline cursor-pointer"}
                      >
                        View Previous Upload
                      </Typography>
                    </a>
                  )}
                  <Typography
                    className="mt-3"
                    state="danger"
                    variant="caption2"
                  >
                    ** Ensure to upload your guarantor ID card and form to
                    complete your registration
                  </Typography>
                  <a
                    href={guarantorForm}
                    download="guarantorForm"
                    style={{ textDecoration: "none" }}
                  >
                    <Typography
                      variant="body2"
                      className="mt-2"
                      state="primary"
                    >
                      Download Guarantor Agreement Form
                    </Typography>
                  </a>
                </>
              )}
            </div>

            <ButtonPair
              loading={isSubmitting}
              onPreviousClick={handlePrevious}
            />
          </Form>
        )}
      </Formik>

      <NotificationModal
        showModal={showModal}
        image={<img src={kycSuccess} alt="" width="120" />}
        onClose={() => {
          navigate(ROUTES.agentOnboardingPending);
          setShowModal(false);
        }}
        headerText="Account setup complete, awaiting verification"
        bodyText="You will be notified about the status of your verification via mail."
      />
    </div>
  );
};

export default FileUploads;
